import { useState } from 'react';

import { useUpdateOperator } from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  Button,
  Field,
  IconButton,
  Input,
  OperatorAvatar,
  PageHeader,
  Select,
  Toggle,
} from '../components/ui/index.js';

export function ProfilePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const updateOp = useUpdateOperator(slug);

  const [tab, setTab] = useState<'profile' | 'security' | 'notifications' | 'devices'>('profile');
  const [name, setName] = useState(user?.name ?? '');
  const [phone, setPhone] = useState('');
  const [saved, setSaved] = useState(false);

  if (!user) return <div>Не авторизован</div>;

  async function handleSaveProfile(): Promise<void> {
    await updateOp.mutateAsync({
      id: user!.id,
      patch: { name: name.trim(), phone: phone.trim() || undefined },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Профиль"
        tabs={[
          {
            key: 'profile',
            label: 'Профиль',
            active: tab === 'profile',
            onClick: () => setTab('profile'),
          },
          {
            key: 'security',
            label: 'Безопасность',
            active: tab === 'security',
            onClick: () => setTab('security'),
          },
          {
            key: 'notifications',
            label: 'Уведомления',
            active: tab === 'notifications',
            onClick: () => setTab('notifications'),
          },
          {
            key: 'devices',
            label: 'Устройства',
            active: tab === 'devices',
            onClick: () => setTab('devices'),
          },
        ]}
      />
      <div className="inbx-page__body" style={{ maxWidth: 680 }}>
        {tab === 'profile' && (
          <>
            <div className="inbx-card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <OperatorAvatar user={user} size="lg" />
              <Button variant="secondary" icon="ti-camera">
                Загрузить фото
              </Button>
            </div>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Личные данные</h4>
              <Field label="Имя" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label="Email" required>
                <Input type="email" value={user.email} readOnly />
              </Field>
              <Field label="Телефон">
                <Input
                  placeholder="+7 ..."
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field>
              <Button variant="primary" onClick={handleSaveProfile} disabled={updateOp.isPending}>
                {saved ? '✓ Сохранено' : updateOp.isPending ? 'Сохранение…' : 'Сохранить'}
              </Button>
            </div>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Рабочие настройки</h4>
              <Field label="Языки поддержки">
                <Select multiple>
                  <option>русский</option>
                  <option>english</option>
                  <option>қазақша</option>
                </Select>
              </Field>
              <Field label="Макс. одновременных тикетов">
                <Input type="number" defaultValue={20} />
              </Field>
            </div>
          </>
        )}

        {tab === 'security' && (
          <>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Пароль</h4>
              <Field label="Текущий пароль">
                <Input type="password" />
              </Field>
              <Field label="Новый пароль">
                <Input type="password" />
              </Field>
              <Field label="Подтвердить">
                <Input type="password" />
              </Field>
              <Button variant="primary">Изменить пароль</Button>
            </div>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Двухфакторная аутентификация</h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <i
                  className="ti-shield-off"
                  style={{ fontSize: 24, color: 'var(--inbx-text-tertiary)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>2FA не включена</div>
                  <div
                    style={{ color: 'var(--inbx-text-tertiary)', fontSize: 'var(--inbx-fz-sm)' }}
                  >
                    Добавь второй фактор для повышенной безопасности
                  </div>
                </div>
                <Button variant="primary">Включить 2FA</Button>
              </div>
            </div>
          </>
        )}

        {tab === 'notifications' && (
          <>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Каналы доставки</h4>
              <NotifRow label="Push на телефон" defaultOn />
              <NotifRow label="Звук в браузере" defaultOn />
              <NotifRow label="Email-дайджест" />
              <NotifRow label="Вибрация" defaultOn />
            </div>
            <div className="inbx-card">
              <h4 className="inbx-card__title">События</h4>
              <NotifRow label="Новый тикет назначен мне" defaultOn />
              <NotifRow label="Ответ клиента в моём тикете" defaultOn />
              <NotifRow label="Упомянули в заметке (@имя)" defaultOn />
              <NotifRow label="SLA сгорает (за 30 минут)" defaultOn />
              <NotifRow label="Эскалация на меня" defaultOn />
              <NotifRow label="Новый тикет без оператора" />
            </div>
            <div className="inbx-card">
              <h4 className="inbx-card__title">Режим «Не беспокоить»</h4>
              <Toggle checked={false} onChange={() => {}} label="Включить расписание" />
              <Toggle checked={true} onChange={() => {}} label="Горящий SLA всё равно будит" />
            </div>
          </>
        )}

        {tab === 'devices' && (
          <div className="inbx-card">
            <h4 className="inbx-card__title">Активные сессии</h4>
            <table className="inbx-table">
              <thead>
                <tr>
                  <th>Устройство</th>
                  <th>Браузер/ОС</th>
                  <th>IP</th>
                  <th>Активность</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>💻 MacBook</td>
                  <td>Chrome / macOS</td>
                  <td>195.x.x.x</td>
                  <td>
                    сейчас{' '}
                    <span
                      className="inbx-pill-badge inbx-pill-badge--info"
                      style={{ marginLeft: 6 }}
                    >
                      текущая
                    </span>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>📱 iPhone</td>
                  <td>Safari / iOS 17</td>
                  <td>78.140.x.x</td>
                  <td>2ч назад</td>
                  <td>
                    <IconButton icon="ti-logout" tooltip="Отвязать" />
                  </td>
                </tr>
                <tr>
                  <td>📱 Android App</td>
                  <td>Chrome / Android</td>
                  <td>91.x.x.x</td>
                  <td>вчера</td>
                  <td>
                    <IconButton icon="ti-logout" tooltip="Отвязать" />
                  </td>
                </tr>
              </tbody>
            </table>
            <Button variant="danger" style={{ marginTop: 12 }}>
              Завершить все остальные сессии
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function NotifRow({ label, defaultOn }: { label: string; defaultOn?: boolean }): JSX.Element {
  const [on, setOn] = useState(defaultOn ?? false);
  return (
    <div style={{ padding: '6px 0', display: 'flex' }}>
      <Toggle checked={on} onChange={setOn} label={label} />
    </div>
  );
}
